// `simple-import-sort` and the prettier import-sort plugin disagree on
// the order of `react` vs the alphabetically-earlier `@mui/...` block.
// Prettier wins (it reformats on save); disable simple-import-sort.
import "./App.css";

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/joy";
import CssBaseline from "@mui/joy/CssBaseline";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { CalendarModalProvider, useCalendarModalState } from "./context/CalendarModalContext";
import { buildChangeLoaderGroups } from "./context/changeLoaderGroups";
import { HashMentionDataProvider } from "./context/HashMentionDataContext";
import {
    MentionGroupModalProvider,
    useMentionGroupModalState,
} from "./context/MentionGroupModalContext";
import { MentionGroupsProvider } from "./context/MentionGroupsContext";
import { TeamEmojiProvider } from "./context/TeamEmojiContext";
import { useHashMentionData } from "./context/useHashMentionData";
import { SessionSupersededOverlay } from "./components/common/SessionSupersededOverlay";
import { FeatureErrorBoundary } from "./components/FeatureErrorBoundary";
import { BillingReturnSnackbar } from "./components/layout/BillingReturnSnackbar";
import { BottomTabBar } from "./components/layout/BottomTabBar";
import { ConnectionStatusSnackbar } from "./components/layout/ConnectionStatusSnackbar";
import { FreezeRouterWhenHidden } from "./components/layout/FreezeRouterWhenHidden";
import { FreezeWhenHidden } from "./components/layout/FreezeWhenHidden";
import { HistoryShell } from "./components/layout/HistoryShell";
import { IdleReloadSnackbar } from "./components/layout/IdleReloadSnackbar";
import { InstallBanner } from "./components/layout/InstallBanner";
import { MentionGroupModal } from "./components/layout/MentionGroupModal";
import { MobileAccountSheet } from "./components/layout/MobileAccountSheet";
import { MobileSpotlightFab } from "./components/layout/MobileSpotlightFab";
import { NonMemberMentionSnackbar } from "./components/layout/NonMemberMentionSnackbar";
import {
    QuickMeetClipboardHandle,
    QuickMeetClipboardHost,
} from "./components/layout/QuickMeetClipboardHost";
import { RequestErrorSnackbar } from "./components/layout/RequestErrorSnackbar";
import { ServiceSwitcherOverlay } from "./components/layout/ServiceSwitcherOverlay";
import { ServiceSwitchLoader } from "./components/layout/ServiceSwitchLoader";
import { ServiceSwitchReady } from "./components/layout/ServiceSwitchReady";
import { Sidebar } from "./components/layout/sidebar";
import { UrlLinkModal } from "./components/modals/UrlLinkModal";
import { AvatarContextProvider } from "./components/ui/avatars/AvatarContext";
import { useAvatarContextValue } from "./components/ui/avatars/useAvatarContextValue";
import { InitialLoad } from "./components/ui/misc/InitialLoad";
import { RouteLoadingFallback } from "./components/ui/misc/RouteLoadingFallback";
import { CalendarModal } from "./features/calendar/components/CalendarModal";
import { useIsV3ChatEnabled } from "./features/channel/chatRolloutFlags";
import { OAUTH_INTEGRATIONS_ENABLED } from "./features/integrations/featureFlags";
import { personalNoteScopes } from "./features/notes/common/utils/noteTypeAlias";
import { SpotlightOverlay } from "./features/spotlight/SpotlightOverlay";
import { SpotlightSettingsModal } from "./features/spotlight/SpotlightSettingsModal";
import { CHAT_TYPE_CODE, SpotlightResult } from "./features/spotlight/types";
import { useSpotlight } from "./features/spotlight/useSpotlight";
import { LazyTaskDiagram } from "./features/tasks/diagram/components/LazyTaskDiagram";
import { loadTeamTaskList } from "./features/tasks/services/loadTaskSearchList";
import { loadTeamProjects } from "./features/tasks/services/loadTeamProjects";
import { projectAvatarSrcMap } from "./features/tasks/utils/projectAvatar";
import { UrlLinkModalProvider } from "./hooks/common/UrlLinkModalContext";
import { useAnalyticsIdentity } from "./hooks/common/useAnalyticsIdentity";
import { useAnalyticsPageviews } from "./hooks/common/useAnalyticsPageviews";
import { AnalyticsPreferencesProvider } from "./hooks/common/useAnalyticsPreferences";
import { useAppBadge } from "./hooks/common/useAppBadge";
import { useAppInitialization } from "./hooks/common/useAppInitialization";
import { BubbleActionsPreferenceProvider } from "./hooks/common/useBubbleActionsPreference";
import { BubbleStylePreferenceProvider } from "./hooks/common/useBubbleStylePreference";
import { useChangeRefresher } from "./hooks/common/useChangeRefresher";
import { DoubleClickTodoPreferenceProvider } from "./hooks/common/useDoubleClickTodoPreference";
import { useGlobalServiceShortcut } from "./hooks/common/useGlobalServiceShortcut";
import { HistoryProvider } from "./hooks/common/useHistory";
import { resolveIdleThresholdMs, useIdleAutoReload } from "./hooks/common/useIdleAutoReload";
import { useKeyboardInset } from "./hooks/common/useKeyboardInset";
import { useNotifications } from "./hooks/common/useNotifications";
import { PersonalGMTagsBootstrap } from "./hooks/common/usePersonalGMTags";
import { useProjectTaskManagement } from "./hooks/common/useProjectTaskManagement";
import { QuickAddRequiredFieldsPreferenceProvider } from "./hooks/common/useQuickAddRequiredFieldsPreference";
import { QuickReactionsPreferenceProvider } from "./hooks/common/useQuickReactionsPreference";
import { useReconcileMyselfAvatar } from "./hooks/common/useReconcileMyselfAvatar";
import { useReportBrowserTimezone } from "./hooks/common/useReportBrowserTimezone";
import { useReportUiLanguage } from "./hooks/common/useReportUiLanguage";
import { useSelfEchoReconcile } from "./hooks/common/useSelfEchoReconcile";
import { useServiceInitialization } from "./hooks/common/useServiceInitialization";
import { SpotlightPreferencesProvider } from "./hooks/common/useSpotlightPreferences";
import { webSocketSync } from "./hooks/common/useSyncManagement";
import { TaskSortPreferencesProvider } from "./hooks/common/useTaskSortPreferences";
import { ThemePreferenceProvider } from "./hooks/common/useThemePreference";
import { useThreadTaskHandling } from "./hooks/common/useThreadTaskHandling";
import { UiSettingsProvider } from "./hooks/common/useUiSettings";
import { useUrlLinkModalState } from "./hooks/common/useUrlLinkModalState";
import { useWakeRefresh } from "./hooks/common/useWakeRefresh";
import { useWebSocket } from "./hooks/common/useWebSocket";
import { useWindowSize } from "./hooks/common/useWindowSize";
import { useInboxResync } from "./hooks/inbox/useInboxResync";
import { useTodoGroups } from "./hooks/useTodoGroups";
import { analytics } from "./services/analytics";
import {
    registerApiHealthListener,
    unregisterApiHealthListener,
    type ApiDownReason,
} from "./services/api";
import { useChannelServiceBootstrap } from "./services/channel/useChannelServiceBootstrap";
import { NotificationsProvider } from "./services/notifications/NotificationsContext";
import { NotificationToastHost } from "./services/notifications/NotificationToastHost";
import { PermissionBanner } from "./services/notifications/PermissionBanner";
import { NotificationIntent } from "./services/notifications/types";
import { startPerfRecorder, type PerfRecorder } from "./services/perf/startPerfRecorder";
import { refreshAllData } from "./services/refreshAllData";
import { useRuntimeConfigBootstrap } from "./services/runtimeConfig/useRuntimeConfig";
import { switchService } from "./services/serviceSwitch/serviceSwitchStore";
import { SearchTeamTasksResponse } from "./types/tasks";
import { canonicalSpotlightHref, milestoneIdFromEntityId } from "./utils/canonicalSpotlightHref";
import { keepIfEqual } from "./utils/keepIfEqual";
import { saveLastWorkspacePath } from "./utils/lastWorkspacePath";
import { parseInternalUrl } from "./utils/parseInternalUrl";
import { spotlightFolderPath, spotlightNotePath } from "./utils/spotlightNotePath";

import { fmt, getMessages, I18nProvider } from "./i18n";
import { ColorThemeProvider } from "./theme/ColorThemeProvider";

// Feature roots are code-split: BlockNote/Yjs (notes), the rich-text editor
// in the chat preview, @hello-pangea/dnd + the task table/board, and the
// inbox view each pull in tens of kilobytes of dependencies that we'd
// otherwise ship in the main bundle. With `lazy` + `Suspense` the chunks
// load on-demand when the user navigates to that route — initial paint
// for the workspace shell stays small.
const ChatHome = lazy(() =>
    import("./features/chat/chatHome").then((m) => ({ default: m.ChatHome }))
);
const InboxHome = lazy(() =>
    import("./features/inbox/inboxHome").then((m) => ({ default: m.InboxHome }))
);
const NoteHome = lazy(() =>
    import("./features/notes/NoteHome").then((m) => ({ default: m.NoteHome }))
);
const TaskHome = lazy(() =>
    import("./features/tasks/taskHome").then((m) => ({ default: m.TaskHome }))
);
const IntegrationsHome = lazy(() =>
    import("./features/integrations/IntegrationsHome").then((m) => ({
        default: m.IntegrationsHome,
    }))
);
const PlansHome = lazy(() =>
    import("./features/billing/PlansHome").then((m) => ({ default: m.PlansHome }))
);
// The Genos main page — the app's landing surface. A plain lazy route
// (all conversation state lives in the App-root useSpotlight instance,
// so unmounting on navigation loses nothing).
const GenosHome = lazy(() =>
    import("./features/genos/GenosHome").then((m) => ({ default: m.GenosHome }))
);
// The v3 chat shell's import graph reaches the message composer and
// the whole BlockNote editor stack (the ~900 kB gzipped vendor-editor
// chunk). Loading it lazily keeps that out of the initial entry — the
// rollout flag it used to be co-located with lives in
// `chatRolloutFlags.ts` precisely so this import can be deferred.
const V3ChatShell = lazy(() =>
    import("./features/channel/V3ChatShell").then((m) => ({ default: m.V3ChatShell }))
);

const API_DOWN_THRESHOLD = 3;

// Chip/citation → in-app URL mapping lives in
// `utils/canonicalSpotlightHref` (extracted for unit testing); keep its
// URL shapes in sync with `handleSpotlightSelect` below.

// The workspace body. Mounted by `App` below, which supplies the one
// context this component reads from its OWN body rather than its JSX —
// see the comment there for why that wrapper has to exist.
const AppBody = () => {
    // Call retained for the hook's resize-listener side effects; the
    // `isTooSmall` gate around `<TooSmallScreen />` is currently
    // commented out (see history) so the return value isn't read.
    useWindowSize();
    const navigate = useNavigate();
    const location = useLocation();
    // App owns the provider below, so use the synchronous catalog accessor
    // for shell-level labels needed before that provider boundary.
    const t = getMessages();

    // Initialize app with authentication and basic setup
    const {
        accessToken,
        myself,
        setMyself,
        useUISM,
        useTEM,
        useMGM,
        useTEJ,
        supersededByTeamName,
    } = useAppInitialization();

    // Analytics: identify the user when their profile is available and
    // emit a $pageview on every route change. Both hooks are passive
    // (`useEffect`-only) and silently no-op when PostHog isn't configured.
    useAnalyticsIdentity(myself);
    useAnalyticsPageviews();

    // Keep the logged-in user's own avatar in sync with the authoritative
    // team-members store, so a second session (or a post-change reload) doesn't
    // render a stale/blank self-avatar from localStorage. See the hook for why.
    useReconcileMyselfAvatar(myself, setMyself, useTEM.teamMemberProfiles);

    // WebSocket management
    const { socketInstance, showDisconnected: showWsDisconnected } = useWebSocket(
        accessToken,
        myself,
        useTEM.currentTeamId
    );

    // v3 messaging stack: opens a parallel `/v3` namespace socket,
    // wires `channelService` (token / user id / socket), registers
    // the inbound event router, and hydrates the in-memory store
    // from IDB. The legacy chat surfaces continue using
    // `socketInstance` above; v3-aware surfaces (mounted via the
    // `useChannel` / `useChannelList` hooks) consume `channelService`
    // directly. Side-by-side until the legacy paths are deleted.
    useChannelServiceBootstrap(accessToken, myself.userId || null, myself.teamId || null);

    // Runtime config: poll `/api/v2/runtime-config` on auth ready and every
    // 60s. Source of truth for the per-chat-type v3 rollout flags and
    // the panic switch. Silent / fail-closed if the endpoint errors.
    useRuntimeConfigBootstrap(accessToken, myself.userId || null);

    // v3 chat gate. True iff build-time env var OR a per-chat-kind
    // runtime flag is rolled out to this user (panic switch overrides).
    // Re-evaluates on each config poll so a server-side rollout flip
    // propagates within ≤60s without a reload.
    const v3ChatEnabled = useIsV3ChatEnabled();

    // API server health tracking
    const [showApiDown, setShowApiDown] = useState(false);
    // Which flavour of unreachable the last failure was, so the banner can
    // say something more useful than "unreachable". Undefined falls back
    // to the generic copy.
    const [apiDownReason, setApiDownReason] = useState<ApiDownReason | undefined>(undefined);
    const apiFailCountRef = useRef(0);
    const apiRecoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleApiHealth = useCallback((isDown: boolean, reason?: ApiDownReason) => {
        if (isDown) {
            apiFailCountRef.current += 1;
            if (apiRecoverTimerRef.current) {
                clearTimeout(apiRecoverTimerRef.current);
                apiRecoverTimerRef.current = null;
            }
            if (apiFailCountRef.current >= API_DOWN_THRESHOLD) {
                setShowApiDown(true);
                // Latest failure wins: a network that drops mid-session
                // reads as "offline" from then on, even though the first
                // failures over the threshold were plain timeouts.
                setApiDownReason(reason);
            }
        } else {
            apiFailCountRef.current = 0;
            if (apiRecoverTimerRef.current) {
                clearTimeout(apiRecoverTimerRef.current);
            }
            apiRecoverTimerRef.current = setTimeout(() => {
                setShowApiDown(false);
                setApiDownReason(undefined);
                apiRecoverTimerRef.current = null;
            }, 1000);
        }
    }, []);

    useEffect(() => {
        registerApiHealthListener(handleApiHealth);
        return () => {
            unregisterApiHealthListener();
            if (apiRecoverTimerRef.current) {
                clearTimeout(apiRecoverTimerRef.current);
            }
        };
    }, [handleApiHealth]);

    // Project and task management. Keys sorted per `sort-keys`.
    const { usePM, useTM, useSM } = useProjectTaskManagement({
        accessToken: accessToken || "",
        currentTeamId: useTEM.currentTeamId,
        myself,
    });

    // Service-specific initialization and management.
    const { useNM, useCM, useIM } = useServiceInitialization({
        accessToken: accessToken || "",
        currentTeamId: useTEM.currentTeamId,
        isLoading: useUISM.isLoading,
        myself,
        socketInstance,
    });

    // Installed-app icon badge. Same two counts the mobile tab bar
    // badges use, so the home-screen number and the in-app ones can't
    // disagree. No-ops in a normal browser tab.
    useAppBadge(useIM.unReadInboxItemCount + useCM.unReadChatAndActivityCounts);

    // Publishes the on-screen keyboard's height as `--keyboard-inset`, so
    // mobile surfaces can keep their composer above it. iOS never resizes
    // the viewport for the keyboard, so nothing in CSS can infer this.
    useKeyboardInset();

    // Tell the server which timezone this user is in. Server-side date
    // math ("due today", and later "send the digest at 8am local") has no
    // other source for it — `TIME_ZONE` is UTC, so without this every
    // boundary is UTC's. Silent and once per session; see the hook.
    useReportBrowserTimezone();

    // Same contract for the UI language: notification emails render in
    // the user's locale, and the server has no other source for it.
    useReportUiLanguage();

    // Daily todo groups — one instance for the whole app: the chat todo
    // pane (prop-drilled through ChatHome) and the agent-input "#"
    // mention picker (via HashMentionDataContext) share it.
    const useTG = useTodoGroups(myself, accessToken);

    // Re-pull for the "#" mention lists. Note metadata and the project
    // list are fetched ONCE at service init and only patched by this
    // user's own writes, so a note or project created by a teammate (or
    // in another tab) stayed missing from the `#` menu until a page
    // reload. Tasks ride along via the open project's task refresh.
    //
    // The team-wide task list lives here rather than in `useTaskManagement`
    // because it belongs to the `#` menu alone: `useTM.allTasks` is the
    // OPEN project's table data, and widening that would change what every
    // table / dashboard / sort reads.
    const [hashTeamTasks, setHashTeamTasks] = useState<SearchTeamTasksResponse[]>([]);
    // Team-wide task list — the only source that spans projects.
    // `project_id=-1` + `top_n=-1` is the same call the task search
    // bars make, including their active-work scope
    // (`include_all=false` hides tasks under a closed parent or
    // milestone), so `#` offers exactly what task search offers.
    const loadHashTeamTasks = async () => {
        const teamTasks: SearchTeamTasksResponse[] = await loadTeamTaskList(
            myself,
            -1,
            "open,wip,blocked,pending",
            -1,
            accessToken,
            false
        );
        // `false` for a failed request (the loader resolves undefined): the
        // change-driven refresh records no token for a load that did not work.
        if (!Array.isArray(teamTasks)) return false;
        // Keep the previous array when nothing changed: this runs on the
        // 60 s refresh, and a new identity re-renders every `#` consumer.
        setHashTeamTasks((prev) => keepIfEqual(prev, teamTasks));
        return true;
    };
    const loadHashTeamTasksRef = useRef(loadHashTeamTasks);
    loadHashTeamTasksRef.current = loadHashTeamTasks;
    // Loaded as soon as there is a session, not on the first refresh
    // trigger: until then `#` offered the open project's tasks only, and
    // the `#` query that kicked the refresh was answered from the empty
    // list, so a task in another project never showed up (MIP-788).
    useEffect(() => {
        if (accessToken && myself?.teamId) void loadHashTeamTasksRef.current();
    }, [accessToken, myself?.teamId]);
    // One entry point for every trigger (window focus, `visibilitychange`, the
    // 60 s tick while visible, the `#` menu's `refresh`): ONE `GET /changes/`
    // call, then only the loaders of the collections whose token moved. See
    // `context/changeRefresher` for the rules (full refresh on the first run, on
    // a team switch, every 10 minutes and whenever `/changes/` fails, which
    // includes an API that has no such endpoint yet).
    //
    // Which loaders each token drives is MIP-970's table. Every loader reports
    // whether it worked: a token is recorded only for a collection whose loaders
    // all did, so a failed one is retried at the next trigger.
    //
    // The project LIST only, deliberately NOT `loadProjectsAndTasks`, which calls
    // `setCurrentProject` when it finds a joined project: a background refresh
    // must never yank the user's open project out from under them. Chats are left
    // out on purpose, the v3 channel service keeps `allChats` live over the socket.
    const refreshHashMentionData = useChangeRefresher({
        accessToken,
        buildGroups: (projectId) =>
            buildChangeLoaderGroups(
                {
                    getChatNoteMeta: () => useNM.getChatNoteMeta(),
                    // Team Notes postdate this refresh and were never added, so a
                    // folder someone shared with you, or a note they filed in one
                    // you can already see, stayed missing until a full reload.
                    getMyNoteMeta: () => useNM.getMyNoteMeta(),
                    getSharedNoteMeta: () => useNM.getSharedNoteMeta(),
                    getTaskNoteMeta: () => useNM.getTaskNoteMeta(),
                    getTeamNoteFolders: () => useNM.getTeamNoteFolders(),
                    getTeamNoteMeta: () => useNM.getTeamNoteMeta(),
                    loadProjects: async () => {
                        const projects = await loadTeamProjects(myself, accessToken);
                        if (!Array.isArray(projects)) return false;
                        usePM.setTeamProjects([...projects]);
                        return true;
                    },
                    loadTeamTasks: () => loadHashTeamTasksRef.current(),
                    // The `since=` delta of the open project's tasks (it rejects when
                    // the request failed) and its sprints. A sprint's status is derived
                    // from today's date on the server, so a bump never announces a
                    // rotation: the backstop and the day-change poll in
                    // `useProjectTaskManagement` are what pick that up.
                    refreshProjectTasks: (id) => usePM.refreshProjectTasks(id),
                    refreshSprints: (id) => useSM.refreshSprintsForProject(id),
                },
                projectId
            ),
        projectId: usePM.currentProject?.projectId,
        teamId: myself?.teamId,
        userId: myself?.userId,
    });

    // Backing data for the "#" mention menu, fed to every editor's
    // `HashSuggestionMenuController` via `HashMentionDataProvider`. See
    // `useHashMentionData` for why it is memoized.
    const hashMentionData = useHashMentionData({
        allChats: useCM.allChats,
        chatNoteMeta: useNM.chatNoteMeta,
        myNoteMeta: useNM.myNoteMeta,
        myself,
        projects: usePM.teamProjects,
        refresh: refreshHashMentionData,
        sharedNoteMeta: useNM.sharedNoteMeta,
        tasks: useTM.allTasks,
        taskNoteMeta: useNM.taskNoteMeta,
        teamNoteMeta: useNM.teamNoteMeta,
        teamTasks: hashTeamTasks,
        todoGroups: useTG.groups,
    });

    // Team roster for the Spotlight ask-input's `@` mention menu. The
    // overlay mounts above AvatarContext, so it can't read
    // teamMemberProfiles from context like the Ask modals do. Memoized:
    // Object.values would otherwise mint a fresh array every render and
    // defeat the mention-source memos downstream.
    const spotlightMentionMembers = useMemo(
        () => Object.values(useTEM.teamMemberProfiles),
        [useTEM.teamMemberProfiles]
    );

    // Project list for the Spotlight filter row's project picker, and the
    // source of the project-LABEL chips on task-related result rows (the
    // search API returns a bare `project_id`; labels only exist here).
    // Threaded as a prop for the same reason as the mention roster above:
    // the overlay mounts outside the project provider tree.
    //
    // `isJoined === true` only — the same predicate the task sidebar's
    // project list uses (`teamProjects` also carries projects the user
    // hasn't joined). Offering a project the viewer isn't a member of
    // would scope the search to something the ACL filter then returns
    // nothing for, which reads as a broken filter rather than an empty
    // project.
    const spotlightFilterProjects = useMemo(
        () => usePM.teamProjects.filter((p) => p.isJoined === true),
        [usePM.teamProjects]
    );

    // Avatars for the rows of that picker. Resolved here because a
    // project's image lives on its PM chat rather than on the project
    // record, and `allChats` is in scope at this level but not inside
    // the overlay. Built as a map in one pass over the chats so an
    // option list is a lookup per row, not a rescan.
    const projectAvatars = useMemo(() => projectAvatarSrcMap(useCM.allChats), [useCM.allChats]);

    // Which space each personal note lives in, for Spotlight's row
    // subtitles. Resolved here for the same reason as the avatars above:
    // My, Shared and Team notes are one backend type indexed one way, so
    // the only thing that tells them apart is which meta list loaded
    // them — and those live on the note manager, not in the overlay.
    const noteScopes = useMemo(
        () => personalNoteScopes(useNM.teamNoteMeta, useNM.sharedNoteMeta),
        [useNM.teamNoteMeta, useNM.sharedNoteMeta]
    );

    // Push freshly-cached IDB data into React state after a background
    // network refresh writes it. Shared by the cache-first boot refresh and
    // the wake refresh — both refresh IDB, then call this to repaint.
    const onIDBRefreshed = useCallback(async () => {
        await Promise.allSettled([
            useCM.funcSetAllChats(),
            useCM.funcSetFlaggedMessages(),
            useCM.funcSetActivityMessages(),
            useTEM.funcSetTeamMembers(),
            // The inbox was missing from this list, and it is the one store
            // whose whole purpose is to show something that arrived while
            // you weren't looking. `useInboxManagement` reads IDB once on
            // mount, so a request that landed in this refresh sat in the
            // database unrendered until the NEXT page load — which is
            // exactly how a cross-team request took two reloads to appear.
            useIM.funcSetInboxItems(),
            usePM.currentProject?.projectId
                ? usePM.refreshProjectTasks(usePM.currentProject.projectId)
                : Promise.resolve(),
        ]);
    }, [useCM, useIM, useTEM, usePM]);
    // Held in a ref so the once-per-team boot effect below doesn't re-run (and
    // re-fire the refresh) every time these manager objects get a new identity.
    const onIDBRefreshedRef = useRef(onIDBRefreshed);
    onIDBRefreshedRef.current = onIDBRefreshed;

    // Cache-first boot: `loadInitialData` renders the shell from IDB, while
    // THIS fires the network hydration in the background — once per team.
    // Living in `App` (not the short-lived `<InitialLoad>`, which unmounts the
    // moment the spinner clears) is what lets the `onIDBRefreshed` re-pull
    // actually run after the fresh data lands. `firstRefreshDone` unblocks the
    // cold-start path in `loadInitialData` (empty cache → wait for this).
    const [firstRefreshDone, setFirstRefreshDone] = useState(false);
    const bootRefreshedTeamRef = useRef<string | null>(null);
    useEffect(() => {
        if (!accessToken || !myself.userId || !useTEM.currentTeamId) return;
        if (bootRefreshedTeamRef.current === useTEM.currentTeamId) return;
        bootRefreshedTeamRef.current = useTEM.currentTeamId;
        setFirstRefreshDone(false);
        let cancelled = false;
        // currentProject is usually not loaded yet at boot; fall back to the
        // last-open project so tasks refresh too.
        const lastProjectId = Number(localStorage.getItem("lastProjectId")) || null;
        void refreshAllData({
            accessToken,
            currentProjectId: usePM.currentProject?.projectId ?? lastProjectId,
            myself,
            onIDBRefreshed: () => onIDBRefreshedRef.current(),
        }).finally(() => {
            if (!cancelled) setFirstRefreshDone(true);
        });
        return () => {
            cancelled = true;
        };
        // `usePM.currentProject` is read only for the initial project id; the
        // team ref guards against re-firing when it (or the token) later change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, myself.userId, useTEM.currentTeamId]);

    // When the user returns from a long idle (closed laptop overnight,
    // backgrounded tab for hours) or after the browser regains network,
    // re-run the boot-time loaders so stale IDB is repopulated from the
    // API. Without this, the chat list / task table / sidebar show
    // whatever was cached before the offline stretch — anything that
    // happened while the WS was disconnected is silently missed.
    useWakeRefresh(() => {
        return refreshAllData({
            accessToken: accessToken || null,
            currentProjectId: usePM.currentProject?.projectId ?? null,
            myself,
            onIDBRefreshed,
        });
    });

    // URL-link modal: opens an in-place preview of internal chat / task /
    // note URLs clicked inside a BlockNote-rendered message. Mounted
    // once globally so the provider sits inside auth gating, all data
    // hooks, BrowserRouter, and every provider above.
    const urlLinkModal = useUrlLinkModalState({ navigate });

    // Fresh-load todo deep link: /workspace/todo/... has no page route
    // (the /workspace/* catch-all renders nothing), so a pasted URL
    // would land on a blank workspace. Mirror how a pasted chat-message
    // URL behaves — open the PAGE, not the preview modal (the modal is
    // reserved for clicked links): route to the self-DM with the todo
    // pane flipped visible and hand the focus target to ToDoPane via
    // sessionStorage — ChatHome/ToDoPane aren't mounted yet on a fresh
    // load, so a CustomEvent would be lost. One-shot; waits for the
    // chat list so the self-DM is resolvable.
    const todoDeepLinkHandledRef = useRef(false);
    useEffect(() => {
        if (todoDeepLinkHandledRef.current) return;
        if (!accessToken || !myself.userId) return;
        if (!window.location.pathname.startsWith("/workspace/todo/")) return;
        const classified = parseInternalUrl(window.location.pathname);
        if (classified.kind !== "todo") {
            todoDeepLinkHandledRef.current = true;
            return;
        }
        const selfDm = useCM.allChats.find(
            (c) => c.chatType === 1 && c.dmPartnerUser?.userId === myself.userId
        );
        // Chat list not loaded yet — the allChats dep re-runs us.
        if (!selfDm) return;
        todoDeepLinkHandledRef.current = true;
        localStorage.setItem("isToDoVisible", "true");
        sessionStorage.setItem(
            "todoDeepLinkTarget",
            JSON.stringify({ itemId: classified.itemId, localDate: classified.localDate })
        );
        // PUNCH LIST (v3 chatId migration): same legacy-slot cast as the
        // Spotlight todo branch below.
        useCM.moveToSpecificChat(
            1,
            selfDm.chatId as unknown as number,
            0,
            false,
            false,
            useTM.setCurrentPreviewTaskId,
            usePM.setCurrentProject,
            undefined
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, myself.userId, useCM.allChats]);

    // Handle thread task interactions
    useThreadTaskHandling({ useCM, useTM });

    // Global keyboard shortcut for switching services. Modifier is Ctrl+Cmd
    // on Mac, Ctrl+Alt on Windows/Linux. Letter shortcuts now compose the
    // navigation with a creation side effect: T opens Tasks and starts a
    // new task; N opens Notes and creates a new top-level My Note. The
    // Cmd+Tab–style cycle gesture (hold Cmd, tap Ctrl) is unchanged.
    // Global Calendar modal state — opened from the keyboard
    // shortcut (Ctrl+Cmd+C / Ctrl+Alt+C), the DM-myself chat
    // header IconButton, and any future entry points. The actual
    // modal renders inside `CalendarModalProvider` further down
    // the tree.
    const calendarModal = useCalendarModalState();

    // Same owner pattern for the @group click-to-open modal — state
    // lives here, provider wraps the editor tree below, the modal
    // itself mounts alongside CalendarModal further down.
    const mentionGroupModal = useMentionGroupModalState();

    // Imperative bridge to the Meet-clipboard handler, which lives
    // inside I18nProvider (so its snackbar text can be translated).
    // The shortcut listener — registered HERE, outside the provider
    // tree — just calls `meetClipboardRef.current?.trigger()`.
    const meetClipboardRef = useRef<QuickMeetClipboardHandle>(null);

    // History modal — opened from the sidebar icon and the global
    // Ctrl+Cmd+H / Ctrl+Alt+H shortcut. Same `{ isOpen, open, close }`
    // shape calendarModal uses; rendered inside <HistoryProvider> via
    // <HistoryShell> further down the tree.
    const [historyOpen, setHistoryOpen] = useState(false);
    const openHistory = useCallback(() => setHistoryOpen(true), []);
    const closeHistory = useCallback(() => setHistoryOpen(false), []);

    // Task graph modal — opened by the Ctrl+Cmd+G / Ctrl+Alt+G shortcut.
    // Anchored on the currently previewed task OR milestone, so it's a
    // no-op when neither is in the preview pane (or the entity is missing
    // a project / backing task id, since the diagram fetches by project
    // and walks the parent tree from a task node).
    const [taskDiagramOpen, setTaskDiagramOpen] = useState(false);

    // Unified diagram target: covers both the task-preview and
    // milestone-preview cases so the shortcut handler and the modal
    // render block share one source of truth. Returns null when no
    // valid entity is previewed — both call sites use that as the gate.
    const taskDiagramTarget = useMemo<{
        projectId: number;
        rootTaskId: number;
        rootLabel: string;
    } | null>(() => {
        if (useTM.currentPreviewKind === "task") {
            const task = useTM.currentPreviewTask;
            if (!task?.id || !task.project?.projectId) return null;
            return {
                projectId: task.project.projectId,
                rootLabel: task.displayId ? `${task.displayId} · ${task.title}` : task.title,
                // Pass the previewed task itself and let the graph loader
                // walk up to the hierarchy root, so the diagram shows the
                // milestone / parent / siblings rather than a lone leaf.
                // Deliberately NOT `task.rootTaskId`: that column is
                // denormalized and a row whose ancestor changed milestone
                // can still hold the root from before the move, which
                // anchored the diagram on a tree the task had left.
                rootTaskId: Number(task.id),
            };
        }
        if (useTM.currentPreviewKind === "milestone") {
            const projectId = usePM.currentProject?.projectId;
            if (!projectId) return null;
            const milestone = useSM.projectMilestones[projectId]?.find(
                (m) => m.milestoneId === useTM.currentPreviewMilestoneId
            );
            if (!milestone || milestone.taskId == null || milestone.projectId == null) {
                return null;
            }
            return {
                projectId: milestone.projectId,
                // Match the label used by the in-pane button in
                // MilestonePreviewInner so the shortcut and click paths
                // open visually identical diagrams.
                rootLabel: fmt(t.app.diagram.label, {
                    title: milestone.title || t.app.diagram.milestoneFallback,
                }),
                rootTaskId: milestone.taskId,
            };
        }
        return null;
    }, [
        useTM.currentPreviewKind,
        useTM.currentPreviewTask,
        useTM.currentPreviewMilestoneId,
        usePM.currentProject?.projectId,
        useSM.projectMilestones,
        t.app.diagram.label,
        t.app.diagram.milestoneFallback,
    ]);

    // Open the user's ToDo pane. The ToDo list has no dedicated route — it
    // lives inside the self-DM chat — so we route to that DM, flip the pane
    // visible in localStorage, and dispatch `openTodoPane` for a ChatHome
    // that's already mounted (chatHome listens for it). Shared by the global
    // shortcut (Ctrl+Cmd/Alt+O) and the sidebar ToDo button; mirrors the
    // `entity_type === "todo"` branch in handleSpotlightPreview but with no
    // specific item to focus.
    const openTodo = useCallback(() => {
        const selfDm = useCM.allChats.find(
            (c) => c.chatType === 1 && c.dmPartnerUser?.userId === myself.userId
        );
        localStorage.setItem("isToDoVisible", "true");
        window.dispatchEvent(new CustomEvent("openTodoPane", { detail: null }));
        if (selfDm) {
            // PUNCH LIST (v3 chatId migration): same legacy `number` cast at
            // the moveToSpecificChat boundary as the other self-DM callers.
            useCM.moveToSpecificChat(
                1,
                selfDm.chatId as unknown as number,
                0,
                false,
                false,
                useTM.setCurrentPreviewTaskId,
                usePM.setCurrentProject,
                undefined
            );
        } else {
            // No self-DM cached yet — drop on the workspace root; the pane
            // opens once the chat list populates and the self-DM resolves.
            navigate("/workspace");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useCM, useTM, usePM, navigate]);

    const { previewIndex: serviceSwitcherPreviewIndex, mruOrder: serviceSwitcherMruOrder } =
        useGlobalServiceShortcut({
            onOpenCalendarModal: calendarModal.open,
            onOpenHistory: openHistory,
            onOpenNotesAndCreate: () => {
                // The create runs right after the navigation, as before: a real
                // switch navigates a frame later (see `switchService`).
                switchService(navigate, "/workspace/notes", () => {
                    void useNM.handleCreateNewMyNote(null);
                });
            },
            onOpenTaskDiagram: () => {
                if (!taskDiagramTarget) return;
                setTaskDiagramOpen(true);
            },
            onOpenTasksAndCreate: () => {
                switchService(navigate, "/workspace/tasks", () => {
                    useTM.handleCreateTask();
                });
            },
            onOpenTodo: openTodo,
            onQuickMeetClipboard: () => meetClipboardRef.current?.trigger(),
        });

    // Click-to-open: jump to the chat / thread / task / inbox that the
    // notification refers to. Lives here because this is the layer that has
    // every store, navigator, and helper in scope at the same time.
    const openIntent = useCallback(
        (intent: NotificationIntent) => {
            // Intents that ship their own reopen callback handle their own
            // click. Agent-run-completion notices (Spotlight / thread / note
            // Ask) set `onOpen` to reopen the exact surface the backgrounded
            // run belongs to — e.g. Spotlight's `() => setIsOpen(true)`, which
            // restores the finished answer from localStorage. These carry NO
            // chat/task/note source, so without this branch they fall through
            // to the `!src` inbox fallback below and wrongly dump the user in
            // the inbox instead of reopening Spotlight.
            if (intent.onOpen) {
                intent.onOpen();
                return;
            }

            const src = intent.source;

            // Inbox: no source -> just switch services.
            if (intent.category === "inbox" || !src) {
                navigate("/workspace/inbox");
                return;
            }

            // Note mentions (surface 6/7/8): open the NOTE, not the task or
            // chat it lives in. MUST branch before the task/chat branches —
            // a task-note mention carries taskId+projectId (which would
            // otherwise open the task) and a chat-note carries chat routing
            // (which would otherwise hit the chat branch). Open via
            // `loadNote(noteType, noteId)` — the same path the Unread sidebar
            // uses — so it works for EVERY note type by id alone, with no
            // dependence on URL params or the backend shipping parent-chat
            // routing. `navigate("/workspace/notes")` just ensures the notes
            // surface is mounted; `loadNote` then opens the specific note and
            // `useNoteRouting` syncs the deep URL.
            if (src.surfaceType === 6 || src.surfaceType === 7 || src.surfaceType === 8) {
                const noteId = src.noteId;
                navigate("/workspace/notes");
                if (noteId !== undefined) {
                    const noteType = src.surfaceType - 5; // 6→1 (my), 7→2 (task), 8→3 (chat)
                    useNM.setCurrentNoteType(noteType);
                    void useNM.loadNote(noteType, noteId, -1);
                }
                return;
            }

            // Task / milestone: navigate to the task deep URL when we have
            // both ids; otherwise fall through to the chat branch.
            if (src.taskId !== undefined && src.projectId !== undefined) {
                navigate(`/workspace/tasks/project/${src.projectId}/task/${src.taskId}`);
                return;
            }

            // Chat / thread: orchestrate via the existing helper which loads
            // the chat and pushes the right deep URL.
            if (src.chatType !== undefined && src.chatId !== undefined) {
                // `src.chatId` is the v3 channel UUID (string). Pass it
                // through unchanged — `Number(uuid)` -> NaN here produced
                // `/workspace/chat/dm/NaN` (same class as the Spotlight
                // branch below; this notification-click branch was missed
                // in that fix). The `as unknown as number` keeps
                // moveToSpecificChat's legacy `chatId: number` param type.
                const numericChatId = src.chatId as unknown as number;
                const threadId = src.threadId ?? 0;
                useCM.moveToSpecificChat(
                    src.chatType,
                    numericChatId,
                    threadId,
                    false,
                    threadId !== 0,
                    useTM.setCurrentPreviewTaskId,
                    usePM.setCurrentProject
                );
                return;
            }

            // Defensive fallback: surface the chat service.
            navigate("/workspace/chat");
        },
        [useCM, useTM, usePM, useNM, navigate]
    );

    // Web notifications: hydrates prefs from backend, owns permission state,
    // and exposes the manager that the websocket router pushes intents to.
    const useNotif = useNotifications(myself, accessToken, openIntent);

    // Live cross-device sync of the user's OWN mutable status: when they toggle
    // appear-offline, edit their custom status, or pause notifications on
    // another device, that device's heartbeat echoes back here and this hook
    // re-fetches the authoritative server value so THIS session updates without
    // a reload. See the hook for why it re-fetches rather than adopting the echo.
    useSelfEchoReconcile(myself, setMyself, accessToken, useNotif.manager);

    // The live path for inbox items no client caused: a reminder fired by the
    // backend cron. Nothing broadcasts those (the `wsType: "inbox"` socket
    // event is relayed by the acting client, and a cron has none), so the
    // reminder sweep and an arriving push ask for a delta sync here instead —
    // which repaints the list and raises the notification the push may have
    // failed to deliver. See `features/inbox/inboxResyncEvent`.
    useInboxResync(myself, accessToken, useNotif.manager, useIM.setInboxItems);

    // Global Cmd-K / Ctrl-K Spotlight overlay. The hook owns open/close
    // state, the keyboard listener, the debounced query, and an `onAsk`
    // stub (Phase 1 just logs; Phase 2 will dispatch the Gemini RAG
    // call). Navigation on result-click is handled here so we have
    // every navigator (useCM / navigate) in scope.
    // The Genos main page renders the same SpotlightContent this hook
    // drives — one conversation, two viewports. `startsWith` (not
    // `includes`) so a hypothetical sub-path stays a match while other
    // services' paths can never collide.
    const isGenosPage = location.pathname.startsWith("/workspace/genos");
    // Remember the workspace URL so a later visit to `/` while still
    // signed in can restore this page (GuestGuard → last path).
    useEffect(() => {
        saveLastWorkspacePath(location.pathname + location.search);
    }, [location.pathname, location.search]);
    const spotlight = useSpotlight({
        accessToken,
        teamId: useTEM.currentTeamId,
        // Handed down explicitly: this hook sits above NotificationsProvider,
        // so it can't reach the manager through context. Used only to
        // announce an answer that finished after the overlay was closed.
        notificationManager: useNotif.manager,
        isPageActive: isGenosPage,
    });
    // The page and the overlay never show simultaneously: navigating to
    // the page (sidebar button, deep link, notification onOpen) closes
    // the overlay. State survives — useSpotlight skips its close-reset
    // while the page is active.
    const spotlightClose = spotlight.close;
    useEffect(() => {
        if (isGenosPage && spotlight.isOpen) spotlightClose();
    }, [isGenosPage, spotlight.isOpen, spotlightClose]);
    // The escalation from the wake refresh above: after a long idle stretch,
    // reload the page outright.
    //
    // `useWakeRefresh` repopulates IDB, which fixes stale DATA. It can't fix
    // a stale PROCESS — timers that never fired, a socket whose reconnect
    // backoff gave up, module caches nobody re-primes, React state built
    // against a bundle we've since redeployed. That residue is what makes the
    // app "behave weird until I refresh", and the only honest fix is the
    // refresh the user would otherwise do by hand. The two hooks also catch
    // DIFFERENT events: `useWakeRefresh` is gated on a hidden→visible
    // transition, which a closed macOS lid frequently never fires (the page
    // stays `visible` while the machine suspends) — `useIdleAutoReload`
    // watches the wall clock instead, so it sees exactly that case.
    //
    // Placed below `useSpotlight` because `isBusy` reads its live state.
    // Gated on `!useUISM.isLoading` so it can't fire mid-boot, when the shell
    // is still assembling and a reload would only restart that work.
    const idleReloadPending = useIdleAutoReload({
        enabled: !useUISM.isLoading,
        idleThresholdMs: resolveIdleThresholdMs(),
        // This app has no `beforeunload` guard anywhere, so a reload destroys
        // in-flight work silently. The losses that can't be undone:
        //   * a streaming Genos answer — only COMPLETED turns are persisted,
        //     so reloading mid-stream drops the partial answer with no resume
        //     path,
        //   * an agent approval awaiting the user — dropping it leaves the
        //     backend run dangling.
        // Typed text is deliberately NOT here: the chat / thread / comment
        // composers and the task-create form persist drafts to localStorage,
        // so a reload keeps them.
        isBusy: () =>
            spotlight.ask.isStreaming ||
            spotlight.ask.pendingApproval != null ||
            spotlight.isLoading,
    });

    // Sidebar "Genos" button + mobile FAB: go to the page (the overlay
    // stays reachable everywhere via Cmd/Ctrl-K).
    const openGenosPage = useCallback(() => {
        spotlightClose();
        switchService(navigate, "/workspace/genos");
    }, [spotlightClose, navigate]);
    // Spotlight-scoped settings modal (LLM model picker + AI-answer
    // toggles), opened from the gear icon on the Spotlight bar. Local
    // to the App root so the dialog can layer above the overlay.
    const [spotlightSettingsOpen, setSpotlightSettingsOpen] = useState(false);
    // Mobile-only account surface (settings, theme, team switch, sign-out).
    // Held here rather than in BottomTabBar because the sheet needs the
    // same myself/socket/manager props the Sidebar already receives.
    const [accountSheetOpen, setAccountSheetOpen] = useState(false);

    // Translate a Spotlight result row into the canonical deep-link URL
    // for its entity type. We set the URL directly (rather than calling
    // feature-level helpers like `useCM.moveToSpecificChat`) so:
    //   • routing logic stays in one place,
    //   • the destination page's existing URL→state effects load and
    //     focus the entity uniformly, regardless of which page we're
    //     coming from, and
    //   • the URL itself reflects the deep link, so reload/back/share
    //     all work.
    //
    // Caveat: SpotlightResult does not yet carry a message_id, so chat
    // hits can focus the right chat/thread but not the exact matching
    // message bubble. Surfacing message_id from the search response is
    // a separate backend change.
    const handleSpotlightSelect = useCallback(
        (r: SpotlightResult) => {
            // A collected past answer has no entity to navigate to — render it
            // inline in the overlay (reusing the live-answer renderer) instead
            // of closing Spotlight. Its own source chips still route normally
            // (they carry chat/task/note/project entity types, not this one).
            if (r.entity_type === "spotlight_answer") {
                spotlight.showStoredAnswer(r);
                return;
            }

            spotlight.close();

            // Milestones deep-link to the milestone route (the id rides
            // in entity_id — see canonicalSpotlightHref for why routing
            // through the backing task broke fresh milestones).
            if (r.entity_type === "milestone" && r.project_id) {
                const milestoneId = milestoneIdFromEntityId(r.entity_id);
                if (milestoneId != null) {
                    navigate(`/workspace/tasks/project/${r.project_id}/milestone/${milestoneId}`);
                    return;
                }
                if (r.task_id) {
                    navigate(`/workspace/tasks/project/${r.project_id}/task/${r.task_id}`);
                    return;
                }
            }

            if (r.entity_type === "task" && r.task_id && r.project_id) {
                navigate(`/workspace/tasks/project/${r.project_id}/task/${r.task_id}`);
                return;
            }

            // Project chips have no preview modal; route to the project's
            // task-list view (the closest "open the project" affordance).
            if (r.entity_type === "project" && r.project_id) {
                navigate(`/workspace/tasks/project/${r.project_id}`);
                return;
            }

            if (r.entity_type === "chat" && r.chat_type && r.chat_id) {
                // Use the chat-management helper rather than a bare
                // `navigate(...)`: for a chat the user hasn't opened
                // this session, `useChatRouting`'s URL→state effect
                // calls `popSpecificMessages` which returns an empty
                // array and then silently early-returns — the URL
                // updates but `currentMainChat` is never set, so the
                // chat surface stays blank. `moveToSpecificChat` sets
                // `currentMainChat` regardless of cache state and
                // performs the navigation itself (now including any
                // `/message/:id` segment and the matching
                // moveToSpecificIndex for scroll-target).
                const chatTypeCode = CHAT_TYPE_CODE[r.chat_type];
                // `chat_id` is the v3 channel UUID (string). It used to be
                // a legacy integer — hence the old `Number(r.chat_id)`,
                // which now yields `NaN` for a UUID and produced
                // `/workspace/chat/dm/NaN`. `moveToSpecificChat` matches on
                // `String(chatId)` against the UUID-keyed chat list and
                // interpolates the id straight into the URL, so pass it
                // through unchanged. The `as unknown as number` keeps the
                // helper's legacy `chatId: number` param type — the same
                // migration shim used wherever v3 UUIDs ride the legacy
                // numeric chat-id slot.
                // `chat_id` (channel), `thread_id` (thread-root Message.id)
                // and `message_id` are all v3 UUID strings now — pass them
                // through so the chip deep-links to the exact thread / message
                // bubble. `moveToSpecificChat` treats ""/undefined as "no
                // focus" and resolves UUID ids directly (the old
                // `Number(...)` coerced UUIDs to NaN and silently dropped the
                // focus, which is why chips only opened the chat top).
                const chatId = r.chat_id;
                const threadId = r.thread_id ?? "";
                const messageId = r.message_id ?? undefined;
                useCM.moveToSpecificChat(
                    chatTypeCode,
                    chatId,
                    threadId,
                    false,
                    Boolean(r.thread_id),
                    useTM.setCurrentPreviewTaskId,
                    usePM.setCurrentProject,
                    messageId
                );
                return;
            }

            if (r.entity_type === "note" && r.note_id) {
                navigate(spotlightNotePath(r, noteScopes));
                return;
            }

            // A folder opens in the notes sidebar (MIP-841).
            const folderPath = spotlightFolderPath(r);
            if (folderPath) {
                navigate(folderPath);
                return;
            }

            // Todos have no dedicated route — the ToDoPane lives inside
            // the user's self-DM. Route to that DM and dispatch a
            // `openTodoPane` event that chatHome consumes to flip the
            // pane visible (it persists in localStorage too so a
            // subsequent reload keeps the user on the pane).
            if (r.entity_type === "todo") {
                const selfDm = useCM.allChats.find(
                    (c) => c.chatType === 1 && c.dmPartnerUser?.userId === myself.userId
                );
                localStorage.setItem("isToDoVisible", "true");
                // Hand the clicked item to ToDoPane so it can scroll to
                // and highlight it: sessionStorage covers a pane that
                // mounts later; the event detail covers one already
                // mounted (its listener consumes the same shape).
                const todoMatch = /^todo:(\d{4}-\d{2}-\d{2})(?::item:(\d+))?$/.exec(
                    r.entity_id || ""
                );
                const focusDetail = todoMatch
                    ? {
                          itemId: todoMatch[2] ? Number(todoMatch[2]) : undefined,
                          localDate: todoMatch[1],
                      }
                    : null;
                if (focusDetail) {
                    sessionStorage.setItem("todoDeepLinkTarget", JSON.stringify(focusDetail));
                }
                window.dispatchEvent(new CustomEvent("openTodoPane", { detail: focusDetail }));
                if (selfDm) {
                    // PUNCH LIST (v3 chatId migration): `selfDm.chatId`
                    // is `string` post-flip; `moveToSpecificChat`'s
                    // signature still takes `number` (its internal
                    // legacy services do too — see the punch-list note
                    // in `useChatManagement`). Cast at the boundary
                    // until that hook's signature flips.
                    useCM.moveToSpecificChat(
                        1,
                        selfDm.chatId as unknown as number,
                        0,
                        false,
                        false,
                        useTM.setCurrentPreviewTaskId,
                        usePM.setCurrentProject,
                        undefined
                    );
                } else {
                    // No self-DM cached locally yet — drop the user on
                    // the workspace root; the chat list will populate
                    // and the pane opens on first self-DM click.
                    navigate("/workspace");
                }
                return;
            }
        },
        // `myself.userId` reads the auth-stable user id — it doesn't
        // change for the lifetime of this component once auth settles,
        // so including it would just churn the callback identity on
        // every render with no behavior change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [spotlight, navigate, useCM, useTM, usePM, noteScopes]
    );

    // Quick-look preview. Opens the existing UrlLinkModal (the same
    // surface chat-message links use) on top of the Spotlight overlay so
    // the user can inspect the entity without losing their place in the
    // conversation. Spotlight sits at z=13100; we pass z=13200 to keep
    // the preview on top.
    //
    // Drives inline citations, answer source chips, AND a plain click on
    // a search result row — the default way to open anything Genos
    // returns. Cmd/Ctrl-click on a row routes to handleSpotlightSelect
    // instead (see SpotlightContent's `handleRowSelect`).
    //
    // Kinds with no modal view fall through to handleSpotlightSelect:
    // projects (closes Spotlight and routes to the task list) and stored
    // answers (rendered inline in the conversation instead).
    const handleSpotlightPreview = useCallback(
        (r: SpotlightResult) => {
            const href = canonicalSpotlightHref(r);
            if (!href) {
                handleSpotlightSelect(r);
                return;
            }
            // The preview's Move-to-page button hands off to the very
            // navigation Cmd/Ctrl-click would have done, so the two
            // routes to a result's page can't drift apart — and the
            // per-kind landing rules (chats via moveToSpecificChat, etc.)
            // stay in one place.
            const outcome = urlLinkModal.openModalByHref(href, {
                onOpenFullPage: () => handleSpotlightSelect(r),
                zIndex: 13200,
            });
            if (outcome !== "opened") {
                // Either parseInternalUrl rejected our URL (shouldn't
                // happen for the shapes we build) or the kind isn't
                // modal-able. Fall back to navigating.
                handleSpotlightSelect(r);
            }
        },
        [handleSpotlightSelect, urlLinkModal]
    );

    // Which top-level service the user is actually viewing. Drives both the
    // keep-alive Home mounting below AND notification active-surface
    // suppression (next effect). Derived from the URL because the Homes are
    // keep-alive — `currentMainChat` etc. stay set on a backgrounded chat
    // page, so the route is the only honest "what am I looking at".
    const activeService = useMemo<"chat" | "tasks" | "notes" | "inbox" | null>(() => {
        const p = location.pathname;
        if (p.includes("/workspace/chat")) return "chat";
        if (p.includes("/workspace/tasks")) return "tasks";
        if (p.includes("/workspace/notes")) return "notes";
        if (p.includes("/workspace/inbox")) return "inbox";
        return null;
    }, [location.pathname]);

    // Tell the manager which chat / thread / task is currently in view so it
    // can suppress notifications for that surface — but ONLY for the service
    // the user is actually ON. The Homes are keep-alive, so `currentMainChat`
    // stays set to the last-open chat even after navigating to the task page;
    // without this route gate that chat counted as the "active surface"
    // forever and its incoming messages were silently swallowed as
    // `ignored-active-surface`. Product rule: suppress only when the current
    // SERVICE matches the notification's service (and the specific object is
    // open) — a notification for a DIFFERENT service always gets through.
    // See [[keepalive-homes-global-flag-leak]].
    useEffect(() => {
        const current =
            activeService === "chat"
                ? useCM.currentThreadChat
                    ? {
                          chatId: String(useCM.currentThreadChat.chatId),
                          chatType: useCM.currentThreadChat.chatType,
                          threadId: useCM.currentThreadChat.threadId,
                      }
                    : useCM.currentMainChat
                      ? {
                            chatId: String(useCM.currentMainChat.chatId),
                            chatType: useCM.currentMainChat.chatType,
                        }
                      : null
                : activeService === "tasks"
                  ? useTM.currentPreviewTaskId
                      ? { taskId: useTM.currentPreviewTaskId }
                      : null
                  : null;
        useNotif.setActiveSurface(current);
    }, [
        activeService,
        useCM.currentMainChat,
        useCM.currentThreadChat,
        useTM.currentPreviewTaskId,
        useNotif,
    ]);

    // WebSocket synchronization. Keys sorted per `sort-keys`.
    webSocketSync({
        accessToken: accessToken,
        currentPreviewTaskId: useTM.currentPreviewTaskId,
        currentProject: usePM.currentProject,
        funcSetInboxItems: useIM.funcSetInboxItems,
        isLoading: useUISM.isLoading,
        myself: myself,
        notificationManager: useNotif.manager,
        setIsTaskCommentUpdated: useTM.setIsTaskCommentUpdated,
        socket: socketInstance,
        useCM: useCM,
        useTEM: useTEM,
    });

    // ---- Keep-alive navigation -------------------------------------------
    // chat/tasks/notes Homes mount on first visit and stay mounted (hidden)
    // thereafter, so switching between them no longer tears down and rebuilds
    // their heavy subtrees (Notes' BlockNote/Yjs editors, the Tasks table, the
    // chat panels) — that rebuild was the multi-second navigation freeze.
    // Inbox stays route-driven (it is light and uses nested <Routes>/useParams).
    // `activeService` is derived once, higher up (it also gates notification
    // active-surface suppression).
    const [mountedHomes, setMountedHomes] = useState<ReadonlySet<string>>(() => new Set());
    // Include the active heavy service synchronously so its first visit paints
    // immediately, without one blank frame before the effect below commits.
    const homesToRender = useMemo<ReadonlySet<string>>(() => {
        if (activeService && activeService !== "inbox" && !mountedHomes.has(activeService)) {
            return new Set(mountedHomes).add(activeService);
        }
        return mountedHomes;
    }, [mountedHomes, activeService]);
    useEffect(() => {
        if (activeService && activeService !== "inbox" && !mountedHomes.has(activeService)) {
            setMountedHomes((prev) => new Set(prev).add(activeService));
        }
    }, [activeService, mountedHomes]);

    // Real-session interaction timing (MIP-957): how long clicks take to paint
    // and settle, and how much of the main thread they block. It reads the
    // browser's own performance observers and posts a few numbers to
    // /client-perf/; see `services/perf/startPerfRecorder.ts`. The refs let
    // the long-lived recorder read the latest token and screen count.
    const perfTokenRef = useRef<string | null>(null);
    perfTokenRef.current = accessToken ?? null;
    const perfHomesRef = useRef(0);
    perfHomesRef.current = homesToRender.size;
    const perfRecorderRef = useRef<PerfRecorder | null>(null);
    useEffect(() => {
        const recorder = startPerfRecorder({
            getToken: () => perfTokenRef.current,
            getHomesMounted: () => perfHomesRef.current,
            apiBase: import.meta.env.VITE_API_BASE_URL,
            isEnabled: () => analytics.isEnabled(),
        });
        perfRecorderRef.current = recorder;
        return () => {
            recorder.stop();
            perfRecorderRef.current = null;
        };
    }, []);
    useEffect(() => {
        perfRecorderRef.current?.navigated(location.pathname);
    }, [location.pathname]);

    // Prewarm the lazy route chunks once the shell is up and the browser is
    // idle, so the first visit to a section doesn't pay the chunk download.
    // React.lazy dedupes, so this only pre-warms.
    useEffect(() => {
        if (useUISM.isLoading) return;
        const w = window as unknown as {
            requestIdleCallback?: (cb: () => void) => number;
            cancelIdleCallback?: (h: number) => void;
        };
        const ric: (cb: () => void) => number =
            w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 200));
        const cic: (h: number) => void = w.cancelIdleCallback ?? window.clearTimeout;
        const handle = ric(() => {
            void import("./features/chat/chatHome");
            void import("./features/tasks/taskHome");
            void import("./features/notes/NoteHome");
            void import("./features/inbox/inboxHome");
            void import("./features/genos/GenosHome");
        });
        return () => cic(handle);
    }, [useUISM.isLoading]);

    // Shared manager bag for the three keep-alive Homes (identical prop set).
    const homeManagers = {
        myself,
        setMyself,
        socket: socketInstance,
        useCM,
        useIM,
        useNM,
        usePM,
        useSM,
        useTEM,
        useTM,
        useUISM,
    };

    // Context values handed to providers below, memoized so a render of
    // `AppBody` that changed none of their inputs does not re-render every
    // consumer (MIP-958). `AppBody` renders about 13 times per task click.
    const avatarContextValue = useAvatarContextValue({
        myself,
        setMyself,
        setTeamMemberProfiles: useTEM.setTeamMemberProfiles,
        socket: socketInstance,
        teamMemberProfiles: useTEM.teamMemberProfiles,
        useCM,
        useUISM,
        selfNotificationsPaused: useNotif.isPausedNow,
    });
    const urlLinkModalContextValue = useMemo(
        () => ({ openModalByHref: urlLinkModal.openModalByHref }),
        [urlLinkModal.openModalByHref]
    );

    // if (isTooSmall) {
    //     return (
    //         <ColorThemeProvider>
    //             <CssBaseline />
    //             <I18nProvider>
    //                 <TooSmallScreen />
    //             </I18nProvider>
    //         </ColorThemeProvider>
    //     );
    // }

    return (
        <>
            <CssBaseline />
            <I18nProvider>
                {/* Blocks the tab when another tab takes over the single
                    browser session with a different team/user (see
                    useMyself). Sits above everything; renders nothing in
                    the normal case. */}
                <SessionSupersededOverlay teamName={supersededByTeamName} />
                <UiSettingsProvider>
                    <ThemePreferenceProvider>
                        <BubbleStylePreferenceProvider>
                            <BubbleActionsPreferenceProvider>
                                <QuickReactionsPreferenceProvider>
                                    <DoubleClickTodoPreferenceProvider>
                                        <QuickAddRequiredFieldsPreferenceProvider>
                                            <TaskSortPreferencesProvider>
                                                {/* SpotlightPreferencesProvider used to sit here.
                                                It now wraps this whole component (see `App` at
                                                the bottom of this file) because `useSpotlight`
                                                above reads it from this component's BODY, where
                                                a provider in the returned JSX is invisible.
                                                Consumers inside are unaffected — SettingsModal
                                                still finds the same context, one level up. */}
                                                <AnalyticsPreferencesProvider>
                                                    <NotificationsProvider value={useNotif}>
                                                        {/* Null-rendering singleton: wires the auth
                                                token into the module-level personal-GM-tags
                                                store (fetch on token change / window focus). */}
                                                        <PersonalGMTagsBootstrap />
                                                        <NotificationToastHost
                                                            subscribeToasts={
                                                                useNotif.subscribeToasts
                                                            }
                                                            onOpenIntent={openIntent}
                                                        />
                                                        <ServiceSwitchLoader />
                                                        <ServiceSwitcherOverlay
                                                            mruOrder={serviceSwitcherMruOrder}
                                                            previewIndex={
                                                                serviceSwitcherPreviewIndex
                                                            }
                                                        />
                                                        {/* Second HashMentionDataProvider: the overlay
                                                mounts above the main provider tree (line
                                                ~1000), so it gets its own wrapper reusing
                                                the same memoized value — the ask input's
                                                `#` mention menu reads it via context. */}
                                                        <HashMentionDataProvider
                                                            value={hashMentionData}
                                                        >
                                                            <SpotlightOverlay
                                                                aiAnswersEnabled={
                                                                    spotlight.aiAnswersEnabled
                                                                }
                                                                ask={spotlight.ask}
                                                                backToHistoryList={
                                                                    spotlight.backToHistoryList
                                                                }
                                                                closeHistory={
                                                                    spotlight.closeHistory
                                                                }
                                                                error={spotlight.error}
                                                                filterProjectIds={
                                                                    spotlight.filterProjectIds
                                                                }
                                                                filterServices={
                                                                    spotlight.filterServices
                                                                }
                                                                historyDetail={
                                                                    spotlight.historyDetail
                                                                }
                                                                historyIsLoading={
                                                                    spotlight.historyIsLoading
                                                                }
                                                                historyLoadFailed={
                                                                    spotlight.historyLoadFailed
                                                                }
                                                                historyMode={spotlight.historyMode}
                                                                historySessions={
                                                                    spotlight.historySessions
                                                                }
                                                                isLoading={spotlight.isLoading}
                                                                isOpen={spotlight.isOpen}
                                                                mentionGroups={
                                                                    useMGM.mentionGroups
                                                                }
                                                                mentionMembers={
                                                                    spotlightMentionMembers
                                                                }
                                                                noteScopes={noteScopes}
                                                                openHistory={spotlight.openHistory}
                                                                projectAvatars={projectAvatars}
                                                                projects={spotlightFilterProjects}
                                                                query={spotlight.query}
                                                                results={spotlight.results}
                                                                turns={spotlight.turns}
                                                                viewHistorySession={
                                                                    spotlight.viewHistorySession
                                                                }
                                                                onApprove={spotlight.onApprove}
                                                                onAsk={spotlight.onAsk}
                                                                onCancel={spotlight.onCancel}
                                                                onChangeFilterProjects={
                                                                    spotlight.onChangeFilterProjects
                                                                }
                                                                onClose={spotlight.close}
                                                                onFeedback={
                                                                    spotlight.submitFeedback
                                                                }
                                                                onNewConversation={
                                                                    spotlight.onNewConversation
                                                                }
                                                                onOpenSettings={() =>
                                                                    setSpotlightSettingsOpen(true)
                                                                }
                                                                onPreview={handleSpotlightPreview}
                                                                onQueryChange={spotlight.setQuery}
                                                                onReject={spotlight.onReject}
                                                                onSelect={handleSpotlightSelect}
                                                                onUndo={spotlight.undoStep}
                                                                onToggleFilterService={
                                                                    spotlight.onToggleFilterService
                                                                }
                                                            />
                                                        </HashMentionDataProvider>
                                                        <SpotlightSettingsModal
                                                            open={spotlightSettingsOpen}
                                                            teamId={useTEM.currentTeamId}
                                                            onClose={() =>
                                                                setSpotlightSettingsOpen(false)
                                                            }
                                                        />
                                                        <ConnectionStatusSnackbar
                                                            apiDownReason={apiDownReason}
                                                            showApiDown={showApiDown}
                                                            showWsDisconnected={showWsDisconnected}
                                                        />
                                                        <IdleReloadSnackbar
                                                            open={idleReloadPending}
                                                        />
                                                        <RequestErrorSnackbar />
                                                        <NonMemberMentionSnackbar
                                                            myself={myself}
                                                            socket={socketInstance}
                                                        />
                                                        <BillingReturnSnackbar />
                                                        <QuickMeetClipboardHost
                                                            ref={meetClipboardRef}
                                                            accessToken={accessToken}
                                                        />
                                                        {useUISM.isLoading ? (
                                                            <InitialLoad
                                                                myself={myself}
                                                                setCurrentMainChat={
                                                                    useCM.setCurrentMainChat
                                                                }
                                                                setIsLoading={useUISM.setIsLoading}
                                                                firstRefreshDone={firstRefreshDone}
                                                            />
                                                        ) : (
                                                            <div className="main-container">
                                                                <PermissionBanner
                                                                    permission={
                                                                        useNotif.permission
                                                                    }
                                                                    masterEnabled={
                                                                        useNotif.preferences
                                                                            .masterEnabled
                                                                    }
                                                                    requestPermission={
                                                                        useNotif.requestPermission
                                                                    }
                                                                />
                                                                <InstallBanner />
                                                                {/* Sidebar lives here (outside <Routes>) so it
                                is mounted once for the whole authenticated
                                shell. Switching services only swaps the
                                routed content next to it instead of
                                remounting the sidebar each time.

                                The whole shell is wrapped in
                                <AvatarContextProvider> so any avatar
                                deep in the tree (chat bubbles, task
                                rows, comments, etc.) can resolve a
                                profile by `userId` from a single source
                                of truth instead of taking a fan-out of
                                props at every callsite. */}
                                                                <AvatarContextProvider
                                                                    value={avatarContextValue}
                                                                >
                                                                    <UrlLinkModalProvider
                                                                        value={
                                                                            urlLinkModalContextValue
                                                                        }
                                                                    >
                                                                        <HashMentionDataProvider
                                                                            value={hashMentionData}
                                                                        >
                                                                            <CalendarModalProvider
                                                                                value={
                                                                                    calendarModal
                                                                                }
                                                                            >
                                                                                <MentionGroupsProvider
                                                                                    value={useMGM}
                                                                                >
                                                                                    <TeamEmojiProvider
                                                                                        value={
                                                                                            useTEJ
                                                                                        }
                                                                                    >
                                                                                        <MentionGroupModalProvider
                                                                                            value={
                                                                                                mentionGroupModal
                                                                                            }
                                                                                        >
                                                                                            <HistoryProvider
                                                                                                teamId={
                                                                                                    useTEM.currentTeamId
                                                                                                }
                                                                                            >
                                                                                                <UrlLinkModal
                                                                                                    myself={
                                                                                                        myself
                                                                                                    }
                                                                                                    useCM={
                                                                                                        useCM
                                                                                                    }
                                                                                                    useNM={
                                                                                                        useNM
                                                                                                    }
                                                                                                    usePM={
                                                                                                        usePM
                                                                                                    }
                                                                                                    useSM={
                                                                                                        useSM
                                                                                                    }
                                                                                                    useTEM={
                                                                                                        useTEM
                                                                                                    }
                                                                                                    useTG={
                                                                                                        useTG
                                                                                                    }
                                                                                                    useTM={
                                                                                                        useTM
                                                                                                    }
                                                                                                    accessToken={
                                                                                                        accessToken
                                                                                                    }
                                                                                                    setMyself={
                                                                                                        setMyself
                                                                                                    }
                                                                                                    socket={
                                                                                                        socketInstance
                                                                                                    }
                                                                                                    target={
                                                                                                        urlLinkModal.target
                                                                                                    }
                                                                                                    useUISM={
                                                                                                        useUISM
                                                                                                    }
                                                                                                    zIndex={
                                                                                                        urlLinkModal.zIndex
                                                                                                    }
                                                                                                    onClose={
                                                                                                        urlLinkModal.closeModal
                                                                                                    }
                                                                                                    onOpenFullPage={
                                                                                                        urlLinkModal.openFullPage ??
                                                                                                        undefined
                                                                                                    }
                                                                                                />
                                                                                                <CalendarModal
                                                                                                    open={
                                                                                                        calendarModal.isOpen
                                                                                                    }
                                                                                                    onClose={
                                                                                                        calendarModal.close
                                                                                                    }
                                                                                                />
                                                                                                <MentionGroupModal
                                                                                                    myself={
                                                                                                        myself
                                                                                                    }
                                                                                                    useCM={
                                                                                                        useCM
                                                                                                    }
                                                                                                    useTEM={
                                                                                                        useTEM
                                                                                                    }
                                                                                                    setMyself={
                                                                                                        setMyself
                                                                                                    }
                                                                                                    socket={
                                                                                                        socketInstance
                                                                                                    }
                                                                                                    useUISM={
                                                                                                        useUISM
                                                                                                    }
                                                                                                />
                                                                                                <HistoryShell
                                                                                                    useCM={
                                                                                                        useCM
                                                                                                    }
                                                                                                    useNM={
                                                                                                        useNM
                                                                                                    }
                                                                                                    usePM={
                                                                                                        usePM
                                                                                                    }
                                                                                                    useSM={
                                                                                                        useSM
                                                                                                    }
                                                                                                    useTM={
                                                                                                        useTM
                                                                                                    }
                                                                                                    open={
                                                                                                        historyOpen
                                                                                                    }
                                                                                                    onClose={
                                                                                                        closeHistory
                                                                                                    }
                                                                                                />
                                                                                                {/* Task graph modal —
                                                                                opened by the
                                                                                Ctrl+Cmd+G /
                                                                                Ctrl+Alt+G
                                                                                shortcut. Gated on
                                                                                `taskDiagramTarget`,
                                                                                which resolves the
                                                                                projectId / rootTaskId
                                                                                / label from either
                                                                                the previewed task
                                                                                or the previewed
                                                                                milestone. */}
                                                                                                {taskDiagramOpen &&
                                                                                                    taskDiagramTarget && (
                                                                                                        <LazyTaskDiagram
                                                                                                            myself={
                                                                                                                myself
                                                                                                            }
                                                                                                            open={
                                                                                                                taskDiagramOpen
                                                                                                            }
                                                                                                            projectId={
                                                                                                                taskDiagramTarget.projectId
                                                                                                            }
                                                                                                            rootLabel={
                                                                                                                taskDiagramTarget.rootLabel
                                                                                                            }
                                                                                                            rootTaskId={
                                                                                                                taskDiagramTarget.rootTaskId
                                                                                                            }
                                                                                                            usePM={
                                                                                                                usePM
                                                                                                            }
                                                                                                            useSM={
                                                                                                                useSM
                                                                                                            }
                                                                                                            useTM={
                                                                                                                useTM
                                                                                                            }
                                                                                                            onClose={() =>
                                                                                                                setTaskDiagramOpen(
                                                                                                                    false
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                    )}
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        display:
                                                                                                            "flex",
                                                                                                        minHeight:
                                                                                                            "100dvh",
                                                                                                        width: "100vw",
                                                                                                    }}
                                                                                                >
                                                                                                    <Sidebar
                                                                                                        myself={
                                                                                                            myself
                                                                                                        }
                                                                                                        setMyself={
                                                                                                            setMyself
                                                                                                        }
                                                                                                        socket={
                                                                                                            socketInstance
                                                                                                        }
                                                                                                        useCM={
                                                                                                            useCM
                                                                                                        }
                                                                                                        useIM={
                                                                                                            useIM
                                                                                                        }
                                                                                                        useTEM={
                                                                                                            useTEM
                                                                                                        }
                                                                                                        useUISM={
                                                                                                            useUISM
                                                                                                        }
                                                                                                        onOpenHistory={
                                                                                                            openHistory
                                                                                                        }
                                                                                                        onOpenGenos={
                                                                                                            openGenosPage
                                                                                                        }
                                                                                                        onOpenTodo={
                                                                                                            openTodo
                                                                                                        }
                                                                                                    />
                                                                                                    <Routes>
                                                                                                        <Route
                                                                                                            path="inbox/*"
                                                                                                            element={
                                                                                                                <Suspense
                                                                                                                    fallback={
                                                                                                                        <RouteLoadingFallback />
                                                                                                                    }
                                                                                                                >
                                                                                                                    <InboxHome
                                                                                                                        myself={
                                                                                                                            myself
                                                                                                                        }
                                                                                                                        setMyself={
                                                                                                                            setMyself
                                                                                                                        }
                                                                                                                        socket={
                                                                                                                            socketInstance
                                                                                                                        }
                                                                                                                        useCM={
                                                                                                                            useCM
                                                                                                                        }
                                                                                                                        useIM={
                                                                                                                            useIM
                                                                                                                        }
                                                                                                                        useTEM={
                                                                                                                            useTEM
                                                                                                                        }
                                                                                                                        useUISM={
                                                                                                                            useUISM
                                                                                                                        }
                                                                                                                    />
                                                                                                                    <ServiceSwitchReady
                                                                                                                        service="inbox"
                                                                                                                        active
                                                                                                                    />
                                                                                                                </Suspense>
                                                                                                            }
                                                                                                        />
                                                                                                        {OAUTH_INTEGRATIONS_ENABLED && (
                                                                                                            <Route
                                                                                                                path="integrations"
                                                                                                                element={
                                                                                                                    <FeatureErrorBoundary
                                                                                                                        feature={
                                                                                                                            t
                                                                                                                                .app
                                                                                                                                .features
                                                                                                                                .integrations
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <IntegrationsHome />
                                                                                                                        </Suspense>
                                                                                                                    </FeatureErrorBoundary>
                                                                                                                }
                                                                                                            />
                                                                                                        )}
                                                                                                        <Route
                                                                                                            path="plans"
                                                                                                            element={
                                                                                                                <FeatureErrorBoundary
                                                                                                                    feature={
                                                                                                                        t
                                                                                                                            .app
                                                                                                                            .features
                                                                                                                            .plans
                                                                                                                    }
                                                                                                                >
                                                                                                                    <Suspense
                                                                                                                        fallback={
                                                                                                                            <RouteLoadingFallback />
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <PlansHome />
                                                                                                                    </Suspense>
                                                                                                                </FeatureErrorBoundary>
                                                                                                            }
                                                                                                        />
                                                                                                        {/* v3 proof-of-life routes. Behind `VITE_USE_V3_CHAT`
                                                                                        — production builds without the env flag don't
                                                                                        even register the routes. The bare `/v3` shows
                                                                                        the chat list with no pane; `/v3/<uuid>` opens
                                                                                        that channel. Selecting a channel from the
                                                                                        sidebar navigates between them. */}
                                                                                                        {v3ChatEnabled && (
                                                                                                            <>
                                                                                                                <Route
                                                                                                                    path="v3"
                                                                                                                    element={
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <V3ChatShell
                                                                                                                                myself={
                                                                                                                                    myself
                                                                                                                                }
                                                                                                                                useNM={
                                                                                                                                    useNM
                                                                                                                                }
                                                                                                                            />
                                                                                                                        </Suspense>
                                                                                                                    }
                                                                                                                />
                                                                                                                <Route
                                                                                                                    path="v3/:channelId"
                                                                                                                    element={
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <V3ChatShell
                                                                                                                                myself={
                                                                                                                                    myself
                                                                                                                                }
                                                                                                                                useNM={
                                                                                                                                    useNM
                                                                                                                                }
                                                                                                                            />
                                                                                                                        </Suspense>
                                                                                                                    }
                                                                                                                />
                                                                                                                <Route
                                                                                                                    path="v3/:channelId/t/:rootMessageId"
                                                                                                                    element={
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <V3ChatShell
                                                                                                                                myself={
                                                                                                                                    myself
                                                                                                                                }
                                                                                                                                useNM={
                                                                                                                                    useNM
                                                                                                                                }
                                                                                                                            />
                                                                                                                        </Suspense>
                                                                                                                    }
                                                                                                                />
                                                                                                            </>
                                                                                                        )}
                                                                                                        {/* The Genos main page. Inside the provider tree
                                                                                        (unlike the overlay), but mention/project data
                                                                                        still rides in as props so SpotlightContent has
                                                                                        one code path across its two hosts. */}
                                                                                                        <Route
                                                                                                            path="genos"
                                                                                                            element={
                                                                                                                <FeatureErrorBoundary
                                                                                                                    feature={
                                                                                                                        t
                                                                                                                            .app
                                                                                                                            .features
                                                                                                                            .genos
                                                                                                                    }
                                                                                                                >
                                                                                                                    <Suspense
                                                                                                                        fallback={
                                                                                                                            <RouteLoadingFallback />
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <GenosHome
                                                                                                                            accessToken={
                                                                                                                                accessToken
                                                                                                                            }
                                                                                                                            mentionGroups={
                                                                                                                                useMGM.mentionGroups
                                                                                                                            }
                                                                                                                            mentionMembers={
                                                                                                                                spotlightMentionMembers
                                                                                                                            }
                                                                                                                            noteScopes={
                                                                                                                                noteScopes
                                                                                                                            }
                                                                                                                            projectAvatars={
                                                                                                                                projectAvatars
                                                                                                                            }
                                                                                                                            projects={
                                                                                                                                spotlightFilterProjects
                                                                                                                            }
                                                                                                                            spotlight={
                                                                                                                                spotlight
                                                                                                                            }
                                                                                                                            teamId={
                                                                                                                                useTEM.currentTeamId
                                                                                                                            }
                                                                                                                            userId={
                                                                                                                                myself.userId
                                                                                                                            }
                                                                                                                            onOpenSettings={() =>
                                                                                                                                setSpotlightSettingsOpen(
                                                                                                                                    true
                                                                                                                                )
                                                                                                                            }
                                                                                                                            onPreview={
                                                                                                                                handleSpotlightPreview
                                                                                                                            }
                                                                                                                            onSelect={
                                                                                                                                handleSpotlightSelect
                                                                                                                            }
                                                                                                                        />
                                                                                                                        <ServiceSwitchReady
                                                                                                                            service="genos"
                                                                                                                            active
                                                                                                                        />
                                                                                                                    </Suspense>
                                                                                                                </FeatureErrorBoundary>
                                                                                                            }
                                                                                                        />
                                                                                                        {/* Default redirect to the Genos main page */}
                                                                                                        <Route
                                                                                                            path=""
                                                                                                            element={
                                                                                                                <Navigate
                                                                                                                    to="genos"
                                                                                                                    replace
                                                                                                                />
                                                                                                            }
                                                                                                        />
                                                                                                        {/* chat/tasks/notes render as keep-alive panes below, not routes;
    swallow their paths so the router doesn't warn about no match. */}
                                                                                                        <Route
                                                                                                            path="*"
                                                                                                            element={
                                                                                                                null
                                                                                                            }
                                                                                                        />
                                                                                                    </Routes>
                                                                                                    {homesToRender.has(
                                                                                                        "chat"
                                                                                                    ) && (
                                                                                                        <div
                                                                                                            style={{
                                                                                                                display:
                                                                                                                    activeService ===
                                                                                                                    "chat"
                                                                                                                        ? "contents"
                                                                                                                        : "none",
                                                                                                            }}
                                                                                                        >
                                                                                                            <FreezeWhenHidden
                                                                                                                active={
                                                                                                                    activeService ===
                                                                                                                    "chat"
                                                                                                                }
                                                                                                            >
                                                                                                                <FreezeRouterWhenHidden
                                                                                                                    active={
                                                                                                                        activeService ===
                                                                                                                        "chat"
                                                                                                                    }
                                                                                                                >
                                                                                                                    <FeatureErrorBoundary
                                                                                                                        feature={
                                                                                                                            t
                                                                                                                                .app
                                                                                                                                .features
                                                                                                                                .chat
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <ChatHome
                                                                                                                                {...homeManagers}
                                                                                                                                isActiveRoute={
                                                                                                                                    activeService ===
                                                                                                                                    "chat"
                                                                                                                                }
                                                                                                                                useTG={
                                                                                                                                    useTG
                                                                                                                                }
                                                                                                                            />
                                                                                                                            <ServiceSwitchReady
                                                                                                                                service="chat"
                                                                                                                                active={
                                                                                                                                    activeService ===
                                                                                                                                    "chat"
                                                                                                                                }
                                                                                                                            />
                                                                                                                        </Suspense>
                                                                                                                    </FeatureErrorBoundary>
                                                                                                                </FreezeRouterWhenHidden>
                                                                                                            </FreezeWhenHidden>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {homesToRender.has(
                                                                                                        "tasks"
                                                                                                    ) && (
                                                                                                        <div
                                                                                                            style={{
                                                                                                                display:
                                                                                                                    activeService ===
                                                                                                                    "tasks"
                                                                                                                        ? "contents"
                                                                                                                        : "none",
                                                                                                            }}
                                                                                                        >
                                                                                                            <FreezeWhenHidden
                                                                                                                active={
                                                                                                                    activeService ===
                                                                                                                    "tasks"
                                                                                                                }
                                                                                                            >
                                                                                                                <FreezeRouterWhenHidden
                                                                                                                    active={
                                                                                                                        activeService ===
                                                                                                                        "tasks"
                                                                                                                    }
                                                                                                                >
                                                                                                                    <FeatureErrorBoundary
                                                                                                                        feature={
                                                                                                                            t
                                                                                                                                .app
                                                                                                                                .features
                                                                                                                                .tasks
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <TaskHome
                                                                                                                                {...homeManagers}
                                                                                                                                isActiveRoute={
                                                                                                                                    activeService ===
                                                                                                                                    "tasks"
                                                                                                                                }
                                                                                                                            />
                                                                                                                            <ServiceSwitchReady
                                                                                                                                service="tasks"
                                                                                                                                active={
                                                                                                                                    activeService ===
                                                                                                                                    "tasks"
                                                                                                                                }
                                                                                                                            />
                                                                                                                        </Suspense>
                                                                                                                    </FeatureErrorBoundary>
                                                                                                                </FreezeRouterWhenHidden>
                                                                                                            </FreezeWhenHidden>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {homesToRender.has(
                                                                                                        "notes"
                                                                                                    ) && (
                                                                                                        <div
                                                                                                            style={{
                                                                                                                display:
                                                                                                                    activeService ===
                                                                                                                    "notes"
                                                                                                                        ? "contents"
                                                                                                                        : "none",
                                                                                                            }}
                                                                                                        >
                                                                                                            <FreezeWhenHidden
                                                                                                                active={
                                                                                                                    activeService ===
                                                                                                                    "notes"
                                                                                                                }
                                                                                                            >
                                                                                                                <FreezeRouterWhenHidden
                                                                                                                    active={
                                                                                                                        activeService ===
                                                                                                                        "notes"
                                                                                                                    }
                                                                                                                >
                                                                                                                    <FeatureErrorBoundary
                                                                                                                        feature={
                                                                                                                            t
                                                                                                                                .app
                                                                                                                                .features
                                                                                                                                .notes
                                                                                                                        }
                                                                                                                    >
                                                                                                                        <Suspense
                                                                                                                            fallback={
                                                                                                                                <RouteLoadingFallback />
                                                                                                                            }
                                                                                                                        >
                                                                                                                            <NoteHome
                                                                                                                                {...homeManagers}
                                                                                                                                isActiveRoute={
                                                                                                                                    activeService ===
                                                                                                                                    "notes"
                                                                                                                                }
                                                                                                                            />
                                                                                                                            <ServiceSwitchReady
                                                                                                                                service="notes"
                                                                                                                                active={
                                                                                                                                    activeService ===
                                                                                                                                    "notes"
                                                                                                                                }
                                                                                                                            />
                                                                                                                        </Suspense>
                                                                                                                    </FeatureErrorBoundary>
                                                                                                                </FreezeRouterWhenHidden>
                                                                                                            </FreezeWhenHidden>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <BottomTabBar
                                                                                                        useCM={
                                                                                                            useCM
                                                                                                        }
                                                                                                        useIM={
                                                                                                            useIM
                                                                                                        }
                                                                                                        onOpenAccount={() =>
                                                                                                            setAccountSheetOpen(
                                                                                                                true
                                                                                                            )
                                                                                                        }
                                                                                                    />
                                                                                                    <MobileAccountSheet
                                                                                                        myself={
                                                                                                            myself
                                                                                                        }
                                                                                                        open={
                                                                                                            accountSheetOpen
                                                                                                        }
                                                                                                        setMyself={
                                                                                                            setMyself
                                                                                                        }
                                                                                                        socket={
                                                                                                            socketInstance
                                                                                                        }
                                                                                                        useCM={
                                                                                                            useCM
                                                                                                        }
                                                                                                        useTEM={
                                                                                                            useTEM
                                                                                                        }
                                                                                                        useUISM={
                                                                                                            useUISM
                                                                                                        }
                                                                                                        onClose={() =>
                                                                                                            setAccountSheetOpen(
                                                                                                                false
                                                                                                            )
                                                                                                        }
                                                                                                    />
                                                                                                    {/* Hidden on the Genos page itself — a
                                                                                    "go to Genos" button there is dead
                                                                                    weight over the conversation. */}
                                                                                                    {!isGenosPage && (
                                                                                                        <MobileSpotlightFab
                                                                                                            onPress={
                                                                                                                openGenosPage
                                                                                                            }
                                                                                                        />
                                                                                                    )}
                                                                                                </Box>
                                                                                            </HistoryProvider>
                                                                                        </MentionGroupModalProvider>
                                                                                    </TeamEmojiProvider>
                                                                                </MentionGroupsProvider>
                                                                            </CalendarModalProvider>
                                                                        </HashMentionDataProvider>
                                                                    </UrlLinkModalProvider>
                                                                </AvatarContextProvider>
                                                            </div>
                                                        )}
                                                    </NotificationsProvider>
                                                </AnalyticsPreferencesProvider>
                                            </TaskSortPreferencesProvider>
                                        </QuickAddRequiredFieldsPreferenceProvider>
                                    </DoubleClickTodoPreferenceProvider>
                                </QuickReactionsPreferenceProvider>
                            </BubbleActionsPreferenceProvider>
                        </BubbleStylePreferenceProvider>
                    </ThemePreferenceProvider>
                </UiSettingsProvider>
            </I18nProvider>
        </>
    );
};

/**
 * Supplies the one context `AppBody` consumes from its own body rather than
 * through its JSX.
 *
 * `AppBody` calls `useSpotlight`, which calls `useSpotlightPreferences`. A
 * provider rendered in `AppBody`'s *returned JSX* is a child of `AppBody` and
 * therefore invisible to hooks running in `AppBody`'s body — React resolves
 * context by position in the mounted tree, not by lexical nesting in a file.
 * So while `SpotlightPreferencesProvider` sat inside that JSX,
 * `useSpotlightPreferences` silently took its no-provider fallback and
 * `aiAnswers` was pinned to `DEFAULT_AI_ANSWERS = true` forever. The Settings
 * toggle wrote to a provider nothing on the ask path could read, which left
 * three documented behaviours dead: the disabled Ask button, its "Enable AI
 * answers in Settings" tooltip, and the `aiOff` placeholder.
 *
 * The fallback is deliberately silent (it exists so consumers mounted outside
 * the App tree don't crash), so nothing warned — and every Spotlight test
 * mocks this hook, so nothing failed either. `SpotlightPreferencesProviderGuard`
 * is the regression guard.
 *
 * Hoisting rather than moving the `useSpotlight` call keeps this contained:
 * consumers inside `AppBody` (SettingsModal) resolve the same context one level
 * up, and `aiAnswers` is the only field any of them reads from a hook above the
 * old provider position — `webSearch` is server-side, read only by Settings.
 *
 * `ColorThemeProvider` lives here for a performance reason (MIP-958). It renders
 * MUI's `CssVarsProvider`, which builds a NEW theme object every time it renders.
 * Inside `AppBody` it re-rendered on every `AppBody` render (about 13 per task
 * click), so every styled component in the app re-rendered, memo or not. One
 * level up it renders only when the theme itself changes.
 */
export const App = () => (
    <ColorThemeProvider>
        <SpotlightPreferencesProvider>
            <AppBody />
        </SpotlightPreferencesProvider>
    </ColorThemeProvider>
);
